import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/data";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 nav-blur transition-all duration-500 ${
        scrolled ? "bg-[#0B0B0B]/85 border-b border-[#C9A34E]/20 py-2" : "bg-[#0B0B0B]/40 py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-1 ring-[#C9A34E]/40 group-hover:ring-[#C9A34E] transition-all">
            <img src={BRAND.logo} alt="Enrich Hair & Beauty" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-lg md:text-xl tracking-wide text-white">Enrich</div>
            <div className="text-[10px] tracking-[0.32em] text-[#C9A34E] uppercase">Hair & Beauty</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `gold-underline text-[13px] tracking-[0.18em] uppercase transition-colors ${
                  isActive ? "text-[#C9A34E] active" : "text-white/85 hover:text-[#C9A34E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/booking"
            data-testid="nav-book-btn"
            className="hidden md:inline-flex items-center gap-2 bg-[#C9A34E] text-[#0B0B0B] hover:bg-[#b08d3e] rounded-full px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors"
          >
            Book Now
          </Link>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-[#C9A34E]"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-[60] bg-[#0B0B0B] transition-transform duration-500 lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center px-6 py-5 border-b border-[#C9A34E]/20">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="w-10 h-10 rounded-full object-cover" />
            <span className="font-display text-xl">Enrich</span>
          </div>
          <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#C9A34E]" aria-label="Close menu">
            <X size={28} />
          </button>
        </div>
        <div className="flex flex-col px-8 py-10 gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-display text-3xl ${isActive ? "text-[#C9A34E]" : "text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/booking"
            data-testid="mobile-book-btn"
            className="mt-6 bg-[#C9A34E] text-[#0B0B0B] rounded-full px-6 py-4 text-center tracking-[0.2em] uppercase text-sm font-medium"
          >
            Book Appointment
          </Link>
        </div>
      </div>
    </header>
  );
}
